import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  Network,
  MessageSquare,
  Bot,
} from 'lucide-react';
import type { ToolState } from '../../types/chat';

interface A2AToolCardProps {
  toolCallId: string;
  state: ToolState;
  input?: Record<string, unknown>;
  output?: unknown;
  errorText?: string;
}

const stateConfig: Record<
  ToolState,
  {
    icon: typeof Loader;
    bgColor: string;
    borderColor: string;
    iconColor: string;
    label: string;
    animate?: boolean;
  }
> = {
  'input-streaming': {
    icon: Loader,
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800',
    iconColor: 'text-violet-400',
    label: 'Preparing',
    animate: true,
  },
  'input-available': {
    icon: Loader,
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-300 dark:border-violet-700',
    iconColor: 'text-violet-500',
    label: 'Delegating',
    animate: true,
  },
  'output-available': {
    icon: CheckCircle,
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800',
    iconColor: 'text-violet-600 dark:text-violet-400',
    label: 'Completed',
  },
  'output-error': {
    icon: XCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-500 dark:text-red-400',
    label: 'Failed',
  },
};

export function A2AToolCard({
  toolCallId,
  state,
  input,
  output,
  errorText,
}: A2AToolCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = stateConfig[state];
  const Icon = config.icon;

  // Extract agent name and message from input
  const agentName = input?.agent_name as string | undefined;
  const delegatedMessage = input?.message as string | undefined;

  const hasDetails = !!delegatedMessage || !!output || !!errorText;

  // Extract response text from output
  const getResponseText = (): string | null => {
    if (!output) return null;
    if (typeof output === 'string') return output;
    if (Array.isArray(output)) {
      const texts = output
        .filter(
          (item): item is { type: string; text: string } =>
            typeof item === 'object' &&
            item !== null &&
            item.type === 'text' &&
            typeof item.text === 'string',
        )
        .map((item) => item.text);
      if (texts.length > 0) return texts.join('\n');
    }
    if (typeof output === 'object' && output !== null) {
      return JSON.stringify(output, null, 2);
    }
    return null;
  };

  const responseText = getResponseText();

  return (
    <div
      className={`my-1 rounded-lg border overflow-hidden transition-all duration-300
        ${config.bgColor} ${config.borderColor}
        ${state === 'input-available' ? 'a2a-delegating' : ''}`}
      data-testid={`a2a-tool-${toolCallId}`}
      data-state={state}
    >
      {/* Header Row */}
      <button
        type="button"
        className={`flex items-center gap-2 px-3 py-2 w-full text-left bg-transparent border-0
          ${hasDetails ? 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/5' : ''}`}
        onClick={hasDetails ? () => setIsExpanded(!isExpanded) : undefined}
        aria-expanded={hasDetails ? isExpanded : false}
        disabled={!hasDetails}
      >
        {/* State Icon */}
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          {config.animate ? (
            <Icon className="w-4 h-4 animate-spin" />
          ) : (
            <Icon className="w-4 h-4" />
          )}
        </div>

        {/* A2A Network Icon */}
        <div className="flex-shrink-0 text-violet-500">
          <Network className="w-4 h-4" />
        </div>

        {/* Label */}
        <span className="font-medium text-sm text-violet-700 dark:text-violet-400">
          Agent-to-Agent
        </span>

        {/* Remote Agent Name Badge */}
        {agentName && (
          <span className="inline-flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 rounded-full">
            <Bot className="w-3 h-3" />
            {agentName}
          </span>
        )}

        {/* State Label */}
        <span className={`text-xs font-medium ${config.iconColor}`}>
          {config.label}
          {state === 'input-available' && '...'}
        </span>

        {/* Expand/Collapse Icon */}
        {hasDetails && (
          <span className="text-gray-400 ml-auto">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        )}
      </button>

      {/* Expanded Details */}
      {isExpanded && hasDetails && (
        <div className="px-3 pb-3 pt-1 border-t border-violet-200/50 dark:border-violet-800/50 space-y-3 bg-white/50 dark:bg-gray-900/50">
          {/* Delegated Message */}
          {delegatedMessage && (
            <div>
              <div className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 mb-1">
                <MessageSquare className="w-3 h-3" />
                Delegated Message
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 rounded-md p-2 border border-violet-100 dark:border-violet-800/50">
                {delegatedMessage}
              </div>
            </div>
          )}

          {/* Response from Remote Agent */}
          {responseText && state === 'output-available' && (
            <div>
              <div className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 mb-1">
                <Bot className="w-3 h-3" />
                Response from {agentName || 'Remote Agent'}
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 rounded-md p-2 border border-violet-100 dark:border-violet-800/50 max-h-48 overflow-y-auto whitespace-pre-wrap">
                {responseText}
              </div>
            </div>
          )}

          {/* Error */}
          {errorText && (
            <div>
              <div className="text-xs font-medium text-red-500 dark:text-red-400 mb-1">Error</div>
              <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md p-2 overflow-x-auto border border-red-100 dark:border-red-800">
                {errorText}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
